import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Calendar, Phone, Info } from 'lucide-react';
import { useLang, t } from '../context/LanguageContext';
import translations from '../i18n/translations';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';
import { BadgeOuverture, BadgeTelephone, JoursAVenir, LigneSamedi, PlagesTelephone, SamedisAVenir } from '../components/HorairesDirect';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function Horaires() {
  const { lang } = useLang();
  const h = translations.horaires;

  return (
    <>
      <SEO {...SEO_TEXTES['/nos-horaires']} path="/nos-horaires" />
    <main data-testid="horaires-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(h.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(h.subtitle, lang)}</p>
            {/* Deux accueils distincts (William, 23/10/2026) : le téléphone en journée, l'atelier en fin de journée. */}
            <div className="mt-6 flex flex-wrap gap-3">
              <BadgeTelephone lang={lang} prefixe={t(h.badge_phone, lang)} />
              <BadgeOuverture lang={lang} prefixe={t(h.badge_workshop, lang)} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Horaires Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Bloc horaires */}
            <motion.div {...fadeUp} className="space-y-6">
              {/* Accueil téléphonique */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8" data-testid="bloc-telephone">
                <div className="flex items-center gap-3 mb-6">
                  <Phone className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.phone_hours_title, lang)}</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <span className="text-slate-300 font-medium">{t(h.weekdays, lang)}</span>
                    <span className="text-[#0099D6] font-semibold"><PlagesTelephone lang={lang} /></span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-slate-300 font-medium">{t(h.saturday, lang)} – {t(h.sunday, lang)}</span>
                    <span className="text-slate-500">{t(h.closed, lang)}</span>
                  </div>
                  <a href="tel:+33183623327" className="inline-flex items-center gap-2 text-[#0099D6] hover:underline font-semibold">
                    <Phone className="w-4 h-4" />
                    01.83.62.33.27
                  </a>
                </div>
              </div>

              {/* Accueil à l'atelier (sans rendez-vous) */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.open_title, lang)}</h2>
                </div>
                <div className="space-y-4">
                  {/* Lundi - Vendredi */}
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <span className="text-slate-300 font-medium">{t(h.weekdays, lang)}</span>
                    <span className="text-[#0099D6] font-semibold">16h30 – 18h00</span>
                  </div>
                  {/* Samedi */}
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <span className="text-slate-300 font-medium">{t(h.saturday, lang)}</span>
                    <span className="text-slate-300 text-right">{t(h.saturday_most, lang)}</span>
                  </div>
                  {/* Dimanche */}
                  <div className="flex items-center justify-between py-3">
                    <span className="text-slate-300 font-medium">{t(h.sunday, lang)}</span>
                    <span className="text-slate-500">{t(h.closed, lang)}</span>
                  </div>
                </div>
              </div>

              {/* Horaires réels, lus dans le portail (chantier #20889) */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.next_days, lang)}</h2>
                </div>
                <p className="text-slate-400 text-sm mb-3"><LigneSamedi lang={lang} texteFixe={t(h.saturday_most, lang)} /></p>
                <JoursAVenir lang={lang} nombre={14} />
                <h3 className="text-lg font-semibold text-white mt-8 mb-2">{t(h.next_saturdays, lang)}</h3>
                <SamedisAVenir lang={lang} nombre={6} />
              </div>

              {/* Note RDV */}
              <div className="bg-[#0099D6]/5 border border-[#0099D6]/20 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#0099D6] mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 text-sm leading-relaxed">{t(h.rdv_note, lang)}</p>
                </div>
              </div>
            </motion.div>

            {/* Bloc adresse + CTA */}
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }} className="space-y-6">
              {/* Adresse */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.address_title, lang)}</h2>
                </div>
                <div className="space-y-3">
                  <p className="text-white font-medium">MÉTÉRIS</p>
                  <p className="text-slate-300">8 Rue Georges Claude</p>
                  <p className="text-slate-300">77100 Meaux</p>
                  <p className="text-slate-300">Seine-et-Marne — Île-de-France</p>
                  <a
                    href="https://www.google.com/maps/place/Meteris+Informatique/@48.9586908,2.9154217,17z/data=!4m15!1m8!3m7!1s0x47e8a17dd3e2741b:0xa2b67f1dba019c7b!2s8+Rue+Georges+Claude,+77100+Meaux!3b1!8m2!3d48.9586908!4d2.9179966!16s%2Fg%2F11bw3y9rp9!3m5!1s0x47e8a3ce34ecae21:0x9451610aa2be2ba8!8m2!3d48.959257!4d2.9177148!16s%2Fg%2F1td4z52c?entry=ttu&g_ep=EgoyMDI2MDMwNC4xIKXMDSoASAFQAw%3D%3D"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-[#0099D6] hover:underline text-sm mt-2"
                  >
                    <MapPin className="w-4 h-4" />
                    {t(h.see_map, lang)}
                  </a>
                </div>
              </div>

              {/* CTA téléphone */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.phone_title, lang)}</h2>
                </div>
                <p className="text-slate-300 text-sm mb-5">{t(h.phone_desc, lang)}</p>
                <a
                  href="tel:+33183623327"
                  className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(0,153,214,0.3)]"
                >
                  <Phone className="w-4 h-4" />
                  01.83.62.33.27
                </a>
              </div>

              {/* CTA Prendre RDV */}
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(h.rdv_title, lang)}</h2>
                </div>
                <p className="text-slate-300 text-sm mb-5">{t(h.rdv_desc, lang)}</p>
                <a
                  href="/prendre-rdv"
                  className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-[#0099D6]/50 hover:bg-[#0099D6]/10 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  <Calendar className="w-4 h-4" />
                  {t(translations.common.rdv_cta, lang)}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
